import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { Disposable, dispose } from 'vscode/vscode/vs/base/common/lifecycle';
import { ResourceMap } from 'vscode/vscode/vs/base/common/map';
import { Promises } from 'vscode/vscode/vs/base/common/async';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { IWorkingCopyBackupService } from 'vscode/vscode/vs/workbench/services/workingCopy/common/workingCopyBackup.service';

let BaseFileWorkingCopyManager = class BaseFileWorkingCopyManager extends Disposable {
    constructor(fileService, logService, workingCopyBackupService) {
        super();
        this.fileService = fileService;
        this.logService = logService;
        this.workingCopyBackupService = workingCopyBackupService;
        this._onDidCreate = this._register(( new Emitter()));
        this.onDidCreate = this._onDidCreate.event;
        this.mapResourceToWorkingCopy = ( new ResourceMap());
        this.mapResourceToDisposeListener = ( new ResourceMap());
    }
    has(resource) {
        return ( this.mapResourceToWorkingCopy.has(resource));
    }
    add(resource, workingCopy) {
        const knownWorkingCopy = this.get(resource);
        if (knownWorkingCopy === workingCopy) {
            return;
        }
        this.mapResourceToWorkingCopy.set(resource, workingCopy);
        this.mapResourceToDisposeListener.get(resource)?.dispose();
        this.mapResourceToDisposeListener.set(resource, workingCopy.onWillDispose(() => this.remove(resource)));
        this._onDidCreate.fire(workingCopy);
    }
    remove(resource) {
        const disposeListener = this.mapResourceToDisposeListener.get(resource);
        if (disposeListener) {
            dispose(disposeListener);
            this.mapResourceToDisposeListener.delete(resource);
        }
        return this.mapResourceToWorkingCopy.delete(resource);
    }
    get workingCopies() {
        return [...( this.mapResourceToWorkingCopy.values())];
    }
    get(resource) {
        return this.mapResourceToWorkingCopy.get(resource);
    }
    dispose() {
        super.dispose();
        this.mapResourceToWorkingCopy.clear();
        dispose(( this.mapResourceToDisposeListener.values()));
        this.mapResourceToDisposeListener.clear();
    }
    async destroy() {
        try {
            await Promises.settled(( this.workingCopies.map(async (workingCopy) => {
                if (workingCopy.isDirty()) {
                    await this.saveWithFallback(workingCopy);
                }
            })));
        }
        catch (error) {
            this.logService.error(error);
        }
        dispose(( this.mapResourceToWorkingCopy.values()));
        this.dispose();
    }
    async saveWithFallback(workingCopy) {
        let saveSuccess = false;
        try {
            saveSuccess = await workingCopy.save();
        }
        catch (error) {
        }
        if (!saveSuccess || workingCopy.isDirty()) {
            const backup = await this.workingCopyBackupService.resolve(workingCopy);
            if (backup) {
                await this.fileService.writeFile(workingCopy.resource, backup.value, { unlock: true });
            }
        }
    }
};
BaseFileWorkingCopyManager = ( __decorate([
    ( __param(0, IFileService)),
    ( __param(1, ILogService)),
    ( __param(2, IWorkingCopyBackupService))
], BaseFileWorkingCopyManager));

export { BaseFileWorkingCopyManager };
